import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Save, Trash2, FileEdit, Stethoscope, Calendar, Award } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import VoiceDictateButton from "@/components/VoiceDictateButton";

const ROLE_LABEL = {
    doctor: "Médico(a)",
    nurse: "Enfermeiro(a)",
    physiotherapist: "Fisioterapeuta",
    occupational_therapist: "Terapeuta Ocupacional",
    nutritionist: "Nutricionista",
    psychologist: "Psicólogo(a)",
    social_worker: "Assistente Social",
    pharmacist: "Farmacêutico(a)",
    transpersonal_therapist: "Terapeuta Transpessoal",
    admin: "Administrador",
};

const PROFESSIONAL_ROLES = new Set(Object.keys(ROLE_LABEL));

const emptyForm = { chief_complaint: "", disease_history: "", important_notes: "", therapeutic_proposal: "" };

export default function EvolutionsSection({ consultationId, currentUser }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [showForm, setShowForm] = useState(false);

    const canWrite = currentUser && PROFESSIONAL_ROLES.has(currentUser.role);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/consultations/${consultationId}/evolutions`);
            setItems(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [consultationId]);

    const startNew = () => { setForm(emptyForm); setEditingId(null); setShowForm(true); };
    const startEdit = (ev) => {
        setForm({
            chief_complaint: ev.chief_complaint || "",
            disease_history: ev.disease_history || "",
            important_notes: ev.important_notes || "",
            therapeutic_proposal: ev.therapeutic_proposal || "",
        });
        setEditingId(ev.id);
        setShowForm(true);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.chief_complaint.trim() && !form.disease_history.trim() && !form.important_notes.trim() && !form.therapeutic_proposal.trim()) {
            toast.error("Preencha ao menos um campo.");
            return;
        }
        setSaving(true);
        try {
            if (editingId) {
                await api.put(`/evolutions/${editingId}`, form);
                toast.success("Evolução atualizada.");
            } else {
                await api.post(`/consultations/${consultationId}/evolutions`, { consultation_id: consultationId, ...form });
                toast.success("Evolução registrada.");
            }
            setShowForm(false); setEditingId(null); setForm(emptyForm);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    const remove = async (id) => {
        if (!window.confirm("Excluir esta evolução?")) return;
        try {
            await api.delete(`/evolutions/${id}`);
            toast.success("Evolução excluída.");
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    // Merge dictated fields with what's already in the form so we never overwrite
    // manually-typed text. If both have content, we append the dictated part on
    // a new line — the doctor can still delete anything unwanted in the review.
    const applyDictation = (dictated) => {
        setForm((prev) => {
            const merge = (existing, incoming) => {
                const a = (existing || "").trim();
                const b = (incoming || "").trim();
                if (!a) return b;
                if (!b) return a;
                return `${a}\n${b}`;
            };
            return {
                chief_complaint: merge(prev.chief_complaint, dictated.chief_complaint),
                disease_history: merge(prev.disease_history, dictated.disease_history),
                important_notes: merge(prev.important_notes, dictated.important_notes),
                therapeutic_proposal: merge(prev.therapeutic_proposal, dictated.therapeutic_proposal),
            };
        });
    };

    if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="animate-spin text-brand" size={28} /></div>;

    return (
        <div className="space-y-4">
            <div className="card-earthy">
                <div className="flex items-start justify-between gap-4 mb-3">
                    <div>
                        <h2 className="font-display text-xl font-medium text-brand flex items-center gap-2"><FileEdit size={18} /> Evoluções da equipe</h2>
                        <p className="text-sm text-muted-clinical">Registros multiprofissionais (médicos, enfermagem, fisioterapia, nutrição, terapia transpessoal e demais).</p>
                    </div>
                    {canWrite && !showForm && (
                        <div className="flex gap-2 flex-wrap">
                            <VoiceDictateButton
                                onApply={(d) => { setForm(emptyForm); setEditingId(null); setShowForm(true); applyDictation(d); }}
                                disabled={saving}
                            />
                            <button data-testid="evolution-new-btn" onClick={startNew} className="btn-primary">
                                <Plus size={14} /> Nova evolução
                            </button>
                        </div>
                    )}
                </div>

                {showForm && canWrite && (
                    <form onSubmit={submit} className="mt-4 space-y-3 bg-alt/50 rounded-xl p-4 border border-[rgba(30,63,51,0.08)]">
                        <div className="flex justify-end -mt-1">
                            <VoiceDictateButton onApply={applyDictation} disabled={saving} />
                        </div>
                        <div>
                            <label className="label-clinical">Queixa principal</label>
                            <textarea data-testid="ev-chief" rows={2} value={form.chief_complaint} onChange={(e) => setForm({ ...form, chief_complaint: e.target.value })} className="input-clinical text-sm" placeholder="Ex: 'Confusão mental leve há 48h, relatada pela família.'" />
                        </div>
                        <div>
                            <label className="label-clinical">História da doença</label>
                            <textarea data-testid="ev-history" rows={3} value={form.disease_history} onChange={(e) => setForm({ ...form, disease_history: e.target.value })} className="input-clinical text-sm" placeholder="Evolução dos sintomas, fatores desencadeantes, cronologia..." />
                        </div>
                        <div>
                            <label className="label-clinical">Anotações importantes</label>
                            <textarea data-testid="ev-notes" rows={3} value={form.important_notes} onChange={(e) => setForm({ ...form, important_notes: e.target.value })} className="input-clinical text-sm" placeholder="Sinais vitais, observações objetivas, achados relevantes..." />
                        </div>
                        <div>
                            <label className="label-clinical">Proposta terapêutica</label>
                            <textarea data-testid="ev-therapeutic" rows={3} value={form.therapeutic_proposal} onChange={(e) => setForm({ ...form, therapeutic_proposal: e.target.value })} className="input-clinical text-sm" placeholder="Ex: 'Hidratação oral 2L/dia, monitorar orientação, retorno em 7 dias.'" />
                        </div>
                        <div className="flex gap-2">
                            <button data-testid="ev-save" disabled={saving} className="btn-primary">
                                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {editingId ? "Salvar alterações" : "Registrar"}
                            </button>
                            <button type="button" onClick={() => { setShowForm(false); setEditingId(null); setForm(emptyForm); }} className="btn-secondary">Cancelar</button>
                        </div>
                    </form>
                )}
            </div>

            {items.length === 0 ? (
                <div className="card-earthy text-sm text-muted-clinical text-center py-8">
                    Nenhuma evolução registrada ainda.
                </div>
            ) : (
                <div className="relative pl-6 space-y-4 before:content-[''] before:absolute before:left-2 before:top-2 before:bottom-2 before:w-px before:bg-[rgba(30,63,51,0.15)]">
                    {items.map((ev) => (
                        <EvolutionCard
                            key={ev.id}
                            ev={ev}
                            currentUser={currentUser}
                            onEdit={() => startEdit(ev)}
                            onRemove={() => remove(ev.id)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function EvolutionCard({ ev, currentUser, onEdit, onRemove }) {
    const canEdit = currentUser && (currentUser.id === ev.author_id || currentUser.role === "admin" || currentUser.role === "doctor");
    const created = new Date(ev.created_at);
    return (
        <div data-testid={`evolution-${ev.id}`} className="relative card-earthy">
            <span className="absolute -left-6 top-6 w-3 h-3 rounded-full bg-brand ring-4 ring-white" />

            {/* Identification block */}
            <div className="mb-3 pb-3 border-b border-[rgba(30,63,51,0.08)]">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                        <div className="flex items-center gap-2 text-brand font-medium">
                            <Stethoscope size={14} /> {ev.author_name}
                        </div>
                        <div className="text-xs text-muted-clinical mt-0.5 flex items-center gap-3 flex-wrap">
                            <span className="inline-flex items-center gap-1"><Award size={11} /> {ev.author_role_label}</span>
                            {ev.author_license && ev.author_license !== "—" && <span>· {ev.author_license}</span>}
                            {ev.author_specialty && <span>· {ev.author_specialty}</span>}
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-clinical inline-flex items-center gap-1"><Calendar size={11} /> {created.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                        {canEdit && (
                            <>
                                <button onClick={onEdit} data-testid={`ev-edit-${ev.id}`} className="btn-ghost text-xs p-1"><FileEdit size={13} /></button>
                                <button onClick={onRemove} data-testid={`ev-remove-${ev.id}`} className="p-1 text-[var(--status-danger)] hover:bg-[var(--status-danger-bg)] rounded"><Trash2 size={13} /></button>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Content blocks */}
            <div className="space-y-3">
                {ev.chief_complaint && <FieldBlock label="Queixa principal" value={ev.chief_complaint} />}
                {ev.disease_history && <FieldBlock label="História da doença" value={ev.disease_history} />}
                {ev.important_notes && <FieldBlock label="Anotações importantes" value={ev.important_notes} />}
                {ev.therapeutic_proposal && <FieldBlock label="Proposta terapêutica" value={ev.therapeutic_proposal} highlight />}
            </div>
        </div>
    );
}

function FieldBlock({ label, value, highlight = false }) {
    return (
        <div className={highlight ? "bg-[#F4EFE6] rounded-lg p-3 border-l-4 border-[var(--brand-accent)]" : ""}>
            <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-brand-secondary mb-1">{label}</div>
            <div className="text-sm text-[var(--text-primary)] whitespace-pre-wrap leading-relaxed">{value}</div>
        </div>
    );
}
